import {
  SegmentActionName,
  SegmentObjectName,
  track,
  TrackingEventName,
  trackLegacyEvent,
} from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { type Period, type SubscriptionTier } from '../../contracts';

export function endTrialModalClosed() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalClosed,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'close',
    },
  });
}

export function endTrialModalDowngradeClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalDowngradeClicked,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'downgrade',
    },
  });
}

export function endTrialModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalOpened,
    },
  });

  // V2 — modal_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
    },
  });
}

export function endTrialModalPeriodChanged(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalPeriodChanged,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'period_changed',
      period: input.period,
    },
  });
}

export function endTrialModalUpgradeClicked(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalUpgradeClicked,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'upgrade',
      period: input.period,
    },
  });
}

// Extend Trial Modal
export function extendTrialModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalOpened,
    },
  });

  // V2 — modal_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
    },
  });
}

export function trialToPaidModalClosed() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalClosed,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'close',
    },
  });
}

export function trialToPaidModalPeriodChanged(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalPeriodChanged,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'period_changed',
      period: input.period,
    },
  });
}

export function trialToPaidModalUpgradeClicked(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalUpgradeClicked,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'upgrade',
      period: input.period,
    },
  });
}

export function settingsBillingCurrentPlanUpgradeClicked(currentPlan: SubscriptionTier) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_current_plan_upgrade',
      action: ButtonAction.SettingsBillingCurrentPlanUpgrade,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: currentPlan,
    },
  });
}

export function settingsBillingEnterpriseGetInTouchClicked(
  currentPlan: SubscriptionTier
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_enterprise_get_in_touch',
      action: ButtonAction.SettingsBillingEnterpriseGetInTouch,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: currentPlan,
    },
  });
}

export function settingsBillingTrialUpgradeNowClick(input: {
  current_plan: SubscriptionTier;
}) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: ButtonAction.SettingsBillingTrialUpgradeNowClicked,
      label: 'upgrade_now',
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: input.current_plan,
    },
  });
}

export function settingsManageBillingClicked(input?: {
  current_plan?: SubscriptionTier;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Settings,
    props: {
      action: SegmentActionName.SettingsManageBillingClicked,
    },
  });

  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_manage_billing',
      action: ButtonAction.SettingsBillingManageBilling,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      ...(input?.current_plan !== undefined ? { current_plan: input.current_plan } : {}),
    },
  });
}

export function settingsTrialBillingCancelTrialClick(input: {
  current_plan: SubscriptionTier;
}) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: ButtonAction.SettingsBillingCancelTrial,
      label: 'cancel_trial',
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: input.current_plan,
    },
  });
}
